import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AppProvider, useApp } from './context/AppContext';

import Navbar        from './components/Navbar';
import Footer        from './components/Footer';
import Home          from './pages/Home';
import JobsPage      from './pages/Jobs';
import JobDetail     from './pages/JobDetail';
import FindHire      from './pages/FindHire';
import SeekAdDetail  from './pages/SeekAdDetail';
import PostSeekAd    from './pages/PostSeekAd';
import { LoginPage, RegisterPage } from './pages/Auth';
import PostJob       from './pages/PostJob';
import Wallet        from './pages/Wallet';
import Dashboard     from './pages/Dashboard';
import Profile       from './pages/Profile';
import Notifications from './pages/Notifications';
import ModeratorPanel from './pages/ModeratorPanel';
import AdminPanel    from './pages/AdminPanel';
import AdminConfig  from './pages/AdminConfig';
import NotFound      from './pages/NotFound';

import { tokens } from './utils/api';
import './index.css';

/* ── scroll to top on route change ─────────────────────────────────────── */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
}

/* ── protected route wrapper ────────────────────────────────────────────── */
function ProtectedRoute({ children, roles }) {
  const { user, loading } = useApp();
  if (loading) return <div className="page-loader"><div className="spinner" /></div>;
  if (!user)   return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

/* ── inner app (needs router context) ───────────────────────────────────── */
function AppInner() {
  const { walletBalance } = useApp();

  return (
    <>
      <ScrollToTop />
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: 'var(--surface)',
            color: 'var(--text)',
            border: '1px solid var(--border)',
            fontFamily: 'var(--font)',
            fontSize: '14px',
            borderRadius: '10px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
          },
          success: { iconTheme: { primary: '#1D9E75', secondary: '#fff' } },
          error:   { iconTheme: { primary: '#E24B4A', secondary: '#fff' } },
          duration: 3500,
        }}
      />

      <Navbar walletBalance={walletBalance} />

      <main style={{ minHeight: 'calc(100vh - var(--nav-h))' }}>
        <Routes>
          {/* ── Public ─────────────────────────────────────────────────── */}
          <Route path="/"              element={<Home />} />
          <Route path="/jobs"          element={<JobsPage />} />
          <Route path="/jobs/:id"      element={<JobDetail />} />
          <Route path="/find-hire"     element={<FindHire />} />
          <Route path="/seek-ads/:id"  element={<SeekAdDetail />} />
          <Route path="/login"         element={<LoginPage />} />
          <Route path="/register"      element={<RegisterPage />} />

          {/* ── Authenticated ──────────────────────────────────────────── */}
          <Route path="/profile"        element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/wallet"         element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
          <Route path="/notifications"  element={<ProtectedRoute><Notifications /></ProtectedRoute>} />

          {/* ── Seeker ─────────────────────────────────────────────────── */}
          <Route path="/post-seek-ad"  element={
            <ProtectedRoute roles={['SEEKER']}>
              <PostSeekAd />
            </ProtectedRoute>
          } />

          {/* ── Poster ─────────────────────────────────────────────────── */}
          <Route path="/post-job"      element={
            <ProtectedRoute roles={['POSTER', 'SUPERADMIN']}>
              <PostJob />
            </ProtectedRoute>
          } />

          {/* ── Dashboard (seeker + poster) ─────────────────────────────── */}
          <Route path="/dashboard"     element={
            <ProtectedRoute roles={['POSTER', 'SEEKER']}>
              <Dashboard />
            </ProtectedRoute>
          } />

          {/* ── Moderator ──────────────────────────────────────────────── */}
          <Route path="/moderator"     element={
            <ProtectedRoute roles={['MODERATOR', 'SUPERADMIN']}>
              <ModeratorPanel />
            </ProtectedRoute>
          } />

          {/* ── Admin ──────────────────────────────────────────────────── */}
          <Route path="/admin/config"  element={
            <ProtectedRoute roles={['SUPERADMIN']}>
              <AdminConfig />
            </ProtectedRoute>
          } />
          <Route path="/admin"         element={
            <ProtectedRoute roles={['SUPERADMIN']}>
              <AdminPanel />
            </ProtectedRoute>
          } />

          {/* ── 404 ────────────────────────────────────────────────────── */}
          <Route path="*"              element={<NotFound />} />
        </Routes>
      </main>

      <Footer />
    </>
  );
}

/* ── root export ─────────────────────────────────────────────────────────── */
export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <AppInner />
      </BrowserRouter>
    </AppProvider>
  );
}
